import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {AnalysisGraphQL, GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {StepDataFrameLineageNode, getTopLevelTableLineages} from 'analysis-tool/data/DatasetLineageUtils';
import {assert} from 'Asserts';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getChildSteps, getTableLineageDescendantSteps} from 'pipelines/StepLineageUtils';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {MenuHeight, MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {MakeFieldsRequired} from 'util/Types';

function isDatasetViewTerminalStep(stepType: PipelineStepType): boolean {
  return [PipelineStepType.VISUALIZATION, PipelineStepType.TABLE_VIEW, PipelineStepType.CHART_VIEW].includes(
    stepType
  );
}

function getSingleOutputStepLineageNode(step: GetAnalysisPipelineStep): StepDataFrameLineageNode {
  const outputStepData = step.outputStepDatas[0];
  const dataFrame = outputStepData.dataFrames[0];

  if (dataFrame == null) {
    return {step};
  }

  return {
    step,
    data: {outputStepData, dataFrame},
  };
}

/** Get lineage nodes that will be displayed as options in the table selector dropdown.
 *
 * @returns Array of {tableNode, viewNodes} objects
 * - {tableNode} The last node in a sequence of nodes that form a top-level table. For example,
 *   if the pipeline looks like INITIALIZE -> FILTER -> RENAME_COLUMN -> PROMOTE, then the terminal
 *   table node is the RENAME_COLUMN step
 * - {viewNodes} List of selectable child nodes of tableNode; criteria is as follows:
 *   - viewNode is a VISUALIZATION step
 *   - viewNode has data (i.e., it is part of a SUCCEEDED sequence of nodes that form a view table)
 */
function getLineageNodes(analysis: AnalysisGraphQL): Array<{
  tableNode: StepDataFrameLineageNode;
  viewNodes: Array<MakeFieldsRequired<StepDataFrameLineageNode, 'data'>>;
}> {
  return (
    getTopLevelTableLineages(analysis)
      // Get the leaf step of each top-level dataset lineage
      .map((tableLineage) => tableLineage[tableLineage.length - 1])
      .filter((terminalTableNode) => terminalTableNode.data != null)
      .map((terminalTableNode) => {
        // Only DATASET_VIEW child steps will start lineages that end with a VISUALIZATION step
        const childViewSteps = getChildSteps(analysis, terminalTableNode.step).filter(
          (step) => step.definition.stepType === PipelineStepType.DATASET_VIEW
        );
        // Valid terminal view steps are "complete" visualization steps
        const viewNodes = childViewSteps
          // Get the last step of each view lineage
          .map((step) => getTableLineageDescendantSteps(analysis, step))
          .map((lineage) => lineage[lineage.length - 1])
          // Only keep visualization steps
          .filter((terminalViewStep) => isDatasetViewTerminalStep(terminalViewStep.definition.stepType))
          // Transform steps into step nodes
          .map((visualizationStep) => getSingleOutputStepLineageNode(visualizationStep))
          // Only keep nodes with data
          .filter((visualizationStepNode) => visualizationStepNode.data != null) as Array<
          MakeFieldsRequired<StepDataFrameLineageNode, 'data'>
        >;

        return {
          tableNode: terminalTableNode as MakeFieldsRequired<StepDataFrameLineageNode, 'data'>,
          viewNodes,
        };
      })
  );
}

function lineageNodeToDisplayName(lineageNode: StepDataFrameLineageNode | null): string {
  if (lineageNode == null) {
    return 'None';
  }

  assert(lineageNode.data != null, 'Expected data frame to exist in lineage node');
  return lineageNode.data.dataFrame.name;
}

function OptionName({node, isView}: {node: StepDataFrameLineageNode | null; isView?: boolean}): JSX.Element {
  return (
    <span className={isView ? css(styles.viewOption) : undefined}>{lineageNodeToDisplayName(node)}</span>
  );
}

function getOptionData(
  node: StepDataFrameLineageNode | null,
  isView?: boolean,
  disabledMessage?: string | null
): OptionData {
  return {
    name: <OptionName node={node} isView={isView} />,
    pureTextName: lineageNodeToDisplayName(node),
    value: node,
    disabled: disabledMessage != null,
    disabledMessage: disabledMessage ?? undefined,
  };
}

export interface AnalysisTableSelectorProps {
  analysis: AnalysisGraphQL;
  selectedLineageNode: MakeFieldsRequired<StepDataFrameLineageNode, 'data'> | null;
  onSelect: (lineageNode: MakeFieldsRequired<StepDataFrameLineageNode, 'data'> | null) => void;
  /**
   * Whether to include views in addition to top-level tables
   */
  includeViews: boolean;
  getDisabledMessage?: (inputLineageNode: StepDataFrameLineageNode) => string | null;
  dataTestAttrs?: DataTestAttrs;
}

/**
 * Selector for tables in an Analysis.
 *
 * A table is represented as a StepDataFrameLineageNode that is the latest node in a lineage.
 */
export default function AnalysisTableSelector(props: AnalysisTableSelectorProps): JSX.Element {
  const lineageNodes = getLineageNodes(props.analysis);

  const optionDatas: Array<OptionData> = [
    getOptionData(null),
    ...lineageNodes.flatMap(({tableNode, viewNodes}) => {
      const disabledMessage = props.getDisabledMessage?.(tableNode);
      return [
        getOptionData(tableNode, false, disabledMessage),
        ...(props.includeViews
          ? viewNodes.map((viewNode) => getOptionData(viewNode, true, disabledMessage))
          : []),
      ];
    }),
  ];

  return (
    <SelectDropdown
      buttonSize={ButtonSize.SMALL}
      menu={
        <SearchableMenu
          height={MenuHeight.MEDIUM}
          width={MenuWidth.FULL}
          selectedValue={props.selectedLineageNode}
          onSelect={props.onSelect}
          optionDatas={optionDatas}
        />
      }
      displayName={lineageNodeToDisplayName(props.selectedLineageNode)}
      dataTestAttrs={props.dataTestAttrs}
    />
  );
}

const styles = StyleSheet.create({viewOption: {paddingLeft: 10}});
